import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import { SuggestTriggerConditionResult } from '@rossum/api-client/internal';
import { QueueRule } from '@rossum/api-client/schemas';
import { CopilotDialog } from '@rossum/components/copilot-dialog/CopilotDialog';
import {
  narrowTriggerConditionSuggestionResultToTemplate,
  useSuggestTriggerCondition,
} from '../hooks/useSuggestTriggerCondition';

type TriggerCopilotProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: (response: SuggestTriggerConditionResult) => void;
  schemaRule: Partial<QueueRule>;
  queue: Url;
};

export const TriggerCopilot = ({
  open,
  onClose,
  onSuccess,
  schemaRule,
  queue,
}: TriggerCopilotProps) => {
  const intl = useIntl();
  const { mutate, isLoading, isError, data } = useSuggestTriggerCondition();

  const onSubmit = useCallback(
    (search: string) => {
      if (search) {
        mutate(
          {
            search,
            schemaRule,
            queue,
          },
          {
            onSuccess: response => {
              const parsedResponse =
                narrowTriggerConditionSuggestionResultToTemplate(response);

              const triggerConditionResult = parsedResponse[0];

              if (triggerConditionResult) {
                onSuccess(triggerConditionResult);
              }
            },
          }
        );
      }
    },
    [mutate, onSuccess, queue, schemaRule]
  );

  return (
    <CopilotDialog
      title={intl.formatMessage({
        id: 'features.queueSettings.rules.triggerCopilot.title',
      })}
      subtitle={intl.formatMessage({
        id: 'features.queueSettings.rules.triggerCopilot.subtitle',
      })}
      errorText={intl.formatMessage({
        id: 'features.queueSettings.rules.triggerCopilot.error',
      })}
      isLoading={isLoading}
      onSubmit={onSubmit}
      onClose={onClose}
      open={open}
      isError={isError || data?.results.length === 0}
      placeholders={([1, 2, 3] as const).map(i =>
        intl.formatMessage({
          id: `features.queueSettings.rules.triggerCopilot.placeholders.${i}`,
        })
      )}
      closeText={intl.formatMessage({
        id: 'features.queueSettings.rules.triggerCopilot.close',
      })}
      confirmText={intl.formatMessage({
        id: 'features.queueSettings.rules.triggerCopilot.confirm',
      })}
    />
  );
};
